
import { Component, Vue } from 'vue-property-decorator';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import FavoriteRepository from '@/api/repositories/FavoriteRepository';

const favoriteRepo: FavoriteRepository = RepositoryFactory.get('favorite');

const CITIES = ['WINDHOEK', 'SWAKOB', 'WALVIS_BAY', 'OSHAKATI', 'ORANJEMUND'];

@Component({})
export default class CityFavoritesView extends Vue {
    public items: any[] = [];
    public loading: boolean = false;
    public search: string = '';
    public cityFilter: string | null = null;

    // Create/edit dialog
    public dialog: boolean = false;
    public saving: boolean = false;
    public editingId: string | null = null;
    public form = { name: '', parent: '', latitude: null as number | null, longitude: null as number | null };

    // Delete dialog
    public deleteDialog: boolean = false;
    public deleting: boolean = false;
    public deletingItem: any = null;

    public get cityOptions() {
        return CITIES.map(c => ({ text: this.$t(`CITY_FAVORITES.PARENT.${c}`).toString(), value: c }));
    }

    public get headers() {
        return [
            { text: this.$t('CITY_FAVORITES.TABLE.NAME'), value: 'name', sortable: true },
            { text: this.$t('CITY_FAVORITES.TABLE.PARENT'), value: 'parent', sortable: true },
            { text: this.$t('CITY_FAVORITES.TABLE.LATITUDE'), value: 'latitude', sortable: false },
            { text: this.$t('CITY_FAVORITES.TABLE.LONGITUDE'), value: 'longitude', sortable: false },
            { text: this.$t('CITY_FAVORITES.TABLE.OPTIONS'), value: 'options', sortable: false, width: '90px' }
        ];
    }

    public get filteredItems() {
        if (!this.cityFilter) return this.items;
        return this.items.filter(f => f.parent === this.cityFilter);
    }

    public get latRules() {
        return [
            (v: any) => {
                if (v === null || v === '' || v === undefined) return true; // nullable
                const n = parseFloat(v);
                return (!isNaN(n) && n >= -90 && n <= 90) || this.$t('CITY_FAVORITES.VALIDATION.LATITUDE_RANGE').toString();
            }
        ];
    }

    public get lngRules() {
        return [
            (v: any) => {
                if (v === null || v === '' || v === undefined) return true;
                const n = parseFloat(v);
                return (!isNaN(n) && n >= -180 && n <= 180) || this.$t('CITY_FAVORITES.VALIDATION.LONGITUDE_RANGE').toString();
            }
        ];
    }

    public cityColor(parent: string): string {
        const map: Record<string, string> = {
            WINDHOEK: 'primary',
            SWAKOB: 'teal',
            WALVIS_BAY: 'blue',
            OSHAKATI: 'purple',
            ORANJEMUND: 'orange darken-2'
        };
        return map[parent] || 'grey';
    }

    public applyFilter() {
        // reactive — filteredItems computed prop handles it
    }

    async created() {
        await this.load();
    }

    public async load() {
        try {
            this.loading = true;
            const response = await favoriteRepo.list();
            this.items = response.data ?? [];
        } catch (e) {
            Vue.notifyErrorSimplified('CITY_FAVORITES.ERROR_LOAD');
        } finally {
            this.loading = false;
        }
    }

    // ── Create / Edit ───────────────────────────────────────────────────────────

    public openCreate() {
        this.editingId = null;
        this.form = { name: '', parent: this.cityFilter || 'WINDHOEK', latitude: null, longitude: null };
        const formRef = this.$refs.form as any;
        if (formRef) formRef.resetValidation();
        this.dialog = true;
    }

    public openEdit(item: any) {
        this.editingId = item.id;
        this.form = {
            name: item.name,
            parent: item.parent,
            latitude: item.latitude,
            longitude: item.longitude
        };
        const formRef = this.$refs.form as any;
        if (formRef) formRef.resetValidation();
        this.dialog = true;
    }

    public async save() {
        const formEl = this.$refs.form as any;
        const valid = formEl ? formEl.validate() : false;
        if (!valid) return;

        const payload = {
            name: this.form.name.trim(),
            parent: this.form.parent,
            latitude: this.form.latitude !== null && this.form.latitude !== undefined && this.form.latitude !== ('' as any)
                ? parseFloat(String(this.form.latitude)) : null,
            longitude: this.form.longitude !== null && this.form.longitude !== undefined && this.form.longitude !== ('' as any)
                ? parseFloat(String(this.form.longitude)) : null
        };

        try {
            this.saving = true;
            if (this.editingId) {
                await favoriteRepo.update(this.editingId, payload);
                Vue.notifySuccessSimplified('CITY_FAVORITES.EDIT_SUCCESS');
            } else {
                await favoriteRepo.create(payload);
                Vue.notifySuccessSimplified('CITY_FAVORITES.CREATE_SUCCESS');
            }
            this.dialog = false;
            await this.load();
        } catch (e) {
            Vue.notifyErrorSimplified(this.editingId ? 'CITY_FAVORITES.EDIT_ERROR' : 'CITY_FAVORITES.CREATE_ERROR');
        } finally {
            this.saving = false;
        }
    }

    // ── Delete ──────────────────────────────────────────────────────────────────

    public confirmDelete(item: any) {
        this.deletingItem = item;
        this.deleteDialog = true;
    }

    public async doDelete() {
        if (!this.deletingItem) return;
        try {
            this.deleting = true;
            await favoriteRepo.delete(this.deletingItem.id);
            Vue.notifySuccessSimplified('CITY_FAVORITES.DELETE_SUCCESS');
            this.deleteDialog = false;
            this.deletingItem = null;
            await this.load();
        } catch (e) {
            Vue.notifyErrorSimplified('CITY_FAVORITES.DELETE_ERROR');
        } finally {
            this.deleting = false;
        }
    }
}
